"use client";

import useCandidates from "@lib/candidates/useCandidates";
import CandidateList from "./CandidateList";
import { useState, useRef } from "react";
import { useDebounce } from "use-debounce";
import {
  Box,
  Divider,
  Stack,
  Typography,
  styled,
} from "@mui/material";
import {
  Tabs,
  TabList,
  Tab,
  SplitButton,
  ListFilterBar,
  ResultCount,
} from "@/ui";
import { TagIcon } from "lucide-react";
import { CSVBoxButton } from "@csvbox/react";
import toast from "react-hot-toast";
import { useRouter } from "next/navigation";
import useRecruiterDetails from "@lib/user/useRecruiterDetails";
import config from "@lib/config";
import ImportFromATS from "./modals/ImportFromATS";
import AddCandidateCV from "./modals/AddCandidateCV";
import AddCandidateManually from "./modals/AddCandidateManually";
import PageTitle from "@components/shared/Typography/PageTitle";
import FadeIn from "@components/shared/loading/FadeIn";
import Paging from "@components/shared/table/Paging";
import useSearch from "@lib/shared/useSearch";
import useUrlListState, { useUrlSync } from "@lib/shared/useUrlListState";
import { PEOPLE_SORT_OPTIONS, TAG_TYPE } from "@lib/enums";
import useImportingStatus from "./hooks/useImportingStatus";
import useTags from "@lib/tags/useTags";

const Container = styled(Box)(({ theme }) => ({
  padding: "40px 46px 40px 46px",

  [theme.breakpoints.down("md")]: {
    padding: "30px 20px",
  },
}));

export default function Candidates() {
  const router = useRouter();
  const { recruiter } = useRecruiterDetails();
  const hasTeamSharing = recruiter?.user?.has_team_sharing ?? false;

  const { initial, updateUrl } = useUrlListState({
    defaultSortProperty: PEOPLE_SORT_OPTIONS.UPDATED_AT,
    defaultSortDirection: "desc",
  });

  const [page, setPage] = useState(initial.page);
  const [activeFilters, setActiveFilters] = useState(initial.filters);
  const [nameSuggestions, setNameSuggestions] = useState([]);
  const [sortBy, setSortBy] = useState(initial.sortBy);

  const { SearchField, search, searchInput, reset: resetSearch } = useSearch(setPage, {
    placeholder: "Search candidates...",
    helperText: "Search by name",
    submitButton: true,
    suggestions: nameSuggestions,
    initialValue: initial.search,
  });

  useUrlSync(updateUrl, { page, sortBy, search, filters: activeFilters });

  const { tags } = useTags({
    page: 1,
    sortBy: { property: "name", direction: "asc" },
    search: "",
    filters: { with_default: true, unique: "slug", type: TAG_TYPE.CANDIDATE },
  });

  const filterFields = [
    {
      key: "tags",
      label: "Tags",
      type: "multiselect",
      icon: <TagIcon size={14} />,
      options: (tags ?? []).map((tag) => ({ value: tag.name, label: tag.name })),
    },
  ];

  const handleFiltersChange = (newFilters) => {
    setActiveFilters(newFilters);
    setPage(1);
  };

  const filteredTagNames = activeFilters
    .filter((f) => f.field === "tags")
    .flatMap((f) => f.values);

  // Suggestion query — debounced so we don't fire a request per keystroke. Sync
  // the derived names into state during render (see React's "You Might Not Need
  // an Effect" — adjusting state when a value changes). Compare by content, not
  // reference: the query returns a fresh [] each render while loading, so a
  // reference check would setState every render and loop.
  const [debouncedSearchInput] = useDebounce(searchInput, 300);
  const { candidates: suggestCandidates } = useCandidates({ page: 1, search: debouncedSearchInput, filters: {}, sortBy });
  const nextNames = (suggestCandidates ?? []).map((c) => c.name);
  if (
    nextNames.length !== nameSuggestions.length ||
    nextNames.some((n, i) => n !== nameSuggestions[i])
  ) {
    setNameSuggestions(nextNames);
  }

  // My Candidates only
  const { candidates, lastPage, total, from, to, hasMore, isLoading, invalidate } =
    useCandidates({
      page,
      sortBy,
      search,
      filters: { ...(filteredTagNames.length > 0 ? { tags: filteredTagNames } : {}) },
    });

  const [addCVOpen, setAddCVOpen] = useState(false);
  const [addManuallyOpen, setAddManuallyOpen] = useState(false);
  const [atsOpen, setAtsOpen] = useState(false);
  const csvLaunchRef = useRef(null);
  const hasUnified = (recruiter?.user?.integration_connections ?? []).some((c) => c.type === "unified");

  const csvUser = { user_id: recruiter?.id, email: recruiter?.user?.email };

  const handleCsvImport = (result, data) => {
    if (result) {
      toast.success(`Importing ${data.row_success} people! The new rows should be visible within a few minutes.`, { duration: 8000 });
      refresh();
    } else {
      toast.error("Import failed. Please try again.");
    }
  };

  const splitItems = [
    { label: "Upload CV", onClick: () => setAddCVOpen(true) },
    { label: "Import CSV", onClick: () => csvLaunchRef.current?.() },
    ...(hasUnified ? [{ label: "Import from ATS", onClick: () => setAtsOpen(true) }] : []),
    { label: "Add manually", onClick: () => setAddManuallyOpen(true) },
  ];

  const resetList = () => {
    resetSearch();
    setActiveFilters([]);
  };
  const { jobs } = useImportingStatus({ setSortBy, resetList, invalidate });

  const handleAddCVClose = () => setAddCVOpen(false);
  const handleAddManuallyClose = () => setAddManuallyOpen(false);

  const refresh = () => {
    setTimeout(() => {
      invalidate();
    }, 3000);
  };

  const handleTabChange = (value) => {
    if (value === "team") {
      router.push("/team/candidates");
    }
  };

  return (
    <Box>
      <FadeIn isLoading={isLoading}>
        <Container>
          <Stack
            sx={{ mb: 2 }}
            direction="row"
            justifyContent={"space-between"}
            alignItems={"center"}
          >
            <PageTitle>Candidates</PageTitle>
            <Box display={"flex"} columnGap={"4px"} alignItems="center">
              <div style={{ position: "absolute", width: 0, height: 0, overflow: "hidden" }}>
                <CSVBoxButton
                  licenseKey={config.csvboxCandidateSheetLicenceKey}
                  user={csvUser}
                  onImport={handleCsvImport}
                  render={(launch) => { csvLaunchRef.current = launch; return <span />; }}
                >
                  csv
                </CSVBoxButton>
              </div>
              <SplitButton
                label="Add candidates"
                startIcon={
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
                    <circle cx="12" cy="7" r="4"/>
                    <path d="M4 21v-1a8 8 0 0116 0v1"/>
                  </svg>
                }
                size="md"
                variant="accent"
                items={splitItems}
                mainOpensMenu
              />
            </Box>
          </Stack>
          <Box sx={{ mb: 6, width: "60%" }}>
            <Typography variant="body2">
              Your candidates are the available talent that can be sent in a
              Float. You can add new candidates manually or import them from a
              PDF or spreadsheet.
            </Typography>
          </Box>
          {hasTeamSharing ? (
            <Tabs value="mine" onChange={handleTabChange}>
              <TabList>
                <Tab value="mine">My candidates</Tab>
                <Tab value="team">Team candidates</Tab>
              </TabList>
            </Tabs>
          ) : (
            <Divider />
          )}
          <Box sx={{ my: 4 }}>
            <Box sx={{ width: '50%', mb: 2 }}>{SearchField}</Box>
            <ListFilterBar
              fields={filterFields}
              filters={activeFilters}
              onChange={handleFiltersChange}
            />
          </Box>
          {!isLoading && candidates?.length > 0 && (
            <ResultCount
              from={from}
              to={to}
              total={total}
              noun="candidate"
            />
          )}
          <CandidateList
            candidates={candidates}
            jobs={jobs}
            sortBy={sortBy}
            setSortBy={setSortBy}
            isLoading={isLoading}
          />
          <Paging
            page={page}
            lastPage={lastPage}
            setPage={setPage}
            hasMore={hasMore}
          />

          <AddCandidateCV open={addCVOpen} onClose={handleAddCVClose} />
          <AddCandidateManually
            open={addManuallyOpen}
            onClose={handleAddManuallyClose}
            recruiterId={recruiter?.id}
            onSwitchToCV={() => { setAddManuallyOpen(false); setAddCVOpen(true); }}
          />
          <ImportFromATS open={atsOpen} onClose={() => setAtsOpen(false)} onImportStarted={refresh} />
        </Container>
      </FadeIn>
    </Box>
  );
}
